import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { ICON, noticeInlineClass } from '@attendance/ui';
import type { ReactNode } from 'react';

/**
 * THE INLINE NOTICE, as one element.
 *
 * `noticeInlineClass` is the skin (see the note on it in `NoticeBar.tsx` for
 * why the skin is a class and not a component). What that class deliberately
 * leaves to the call site — the tone triple and the glyph beside the text — was
 * then spelled out by hand at roughly twenty call sites, four words of Tailwind
 * and a `<span>` wrapper at a time. This is that spelling, once.
 *
 * WHY THE TRIPLE AND NOT THE SOLID HUE. Most of these were `text-copy
 * text-danger` before: the solid hue as running ink, which is colour alone and
 * what rules §1 bans, and which §1 measures at ~1.3:1 once it sits on a tinted
 * card. `-on-surface` is the ink designed for a `-surface` tint; the border is
 * the only thing separating one notice from the next on a site-office laptop,
 * which is why the triple is all-or-nothing rather than a tint.
 *
 * WHY A GLYPH. Same rule: the tone has to survive being read in greyscale, so
 * every notice carries the mark of its tone as well as its colour. `mt-0.5`
 * pins it to the FIRST LINE rather than the middle of a block that wraps to
 * three lines in Arabic — the reason the skin is `items-start`.
 */
const TONE = {
  danger: 'border-danger-border bg-danger-surface text-danger-on-surface',
  warning: 'border-warning-border bg-warning-surface text-warning-on-surface',
  success: 'border-success-border bg-success-surface text-success-on-surface',
  info: 'border-info-border bg-info-surface text-info-on-surface',
} as const;

const GLYPH = {
  danger: AlertTriangle,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
} as const;

export type NoticeTone = keyof typeof TONE;

export function InlineNotice({
  tone = 'danger',
  id,
  role,
  className,
  children,
}: {
  tone?: NoticeTone;
  id?: string;
  /** `alert` for anything that appears in response to an action the user took. */
  role?: string;
  /** Spacing to the block above only — never a colour, never an inset. */
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const Glyph = GLYPH[tone];
  return (
    <div
      id={id}
      role={role}
      className={`${noticeInlineClass} ${TONE[tone]}${className ? ` ${className}` : ''}`}
    >
      <Glyph size={ICON.control} className="mt-0.5 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-element">{children}</div>
    </div>
  );
}
